import React, { useEffect } from "react";
import { useAlert } from "../components/AlertModal";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/PrintPurchaseOrderPageShop.css";

const PrintPurchaseOrderPageShop = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, customer } = location.state || {};
  const { showAlert } = useAlert();

  useEffect(() => {
    if (!data || !customer) {
      showAlert("印刷データが見つかりません");
      navigate(-1);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        if (window.electronAPI?.printKobutsu) {
          await window.electronAPI.printKobutsu();
        } else {
          window.print();
        }
      } catch (error) {
        console.error("古物台帳印刷エラー:", error);
      } finally {
        navigate(-1);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [data, customer, navigate, showAlert]);

  if (!data || !customer) return null;

  const {
    purchase_number,
    date,
    total_items,
    total_amount,
    purchase_bonus,
    shipping_adjustment,
    details = [],
  } = data;

  const {
    name,
    kana,
    furigana,
    postal_code,
    address,
    phone_number,
    email,
    birth_date,
    age,
    invoice_number,
    occupation,
    identification_type,
  } = customer;

  const customerKana = kana || furigana || "";

  // ========================================================
  // 名前に含まれる (T番号)(J番号) を解析
  // ========================================================

  const occupationCodeMap = {
    J01: "会社員",
    J02: "公務員",
    J03: "アルバイト",
    J04: "学生",
    J05: "個人事業主",
    J00: "無職",
  };

  const rawName = name || "";

  // (T1234567890123) / （T1234567890123）
  // の両方に対応
  const invoiceMatch = rawName.match(
    /[（(]\s*(T\d+)\s*[)）]/i
  );

  // (J01) ～ (J05)、(J00)
  const occupationMatch = rawName.match(
    /[（(]\s*(J(?:00|01|02|03|04|05))\s*[)）]/i
  );

  // 印刷用氏名
  // (T...) と (Jxx) は名前から削除
  const printName = rawName
    .replace(/[（(]\s*T\d+\s*[)）]/gi, "")
    .replace(/[（(]\s*J(?:00|01|02|03|04|05)\s*[)）]/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  // インボイス番号
  // 名前内のT番号を優先
  const printInvoiceNumber =
    invoiceMatch?.[1]?.toUpperCase() ||
    invoice_number ||
    "";

  // 職業
  const occupationCode =
    occupationMatch?.[1]?.toUpperCase() || "";

  const printOccupation =
    occupationCodeMap[occupationCode] ||
    occupation ||
    "";

  const itemTotal =
    Number(total_amount || 0) -
    Number(purchase_bonus || 0) -
    Number(shipping_adjustment || 0);

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString("ja-JP");
  
  const MONEY_BOXES = 7;

  const renderMoneyBoxes = (value, show = true) => {
    let boxes = Array(MONEY_BOXES).fill("");

    if (
      show &&
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      const digits = Math.round(Number(value || 0))
        .toString()
        .replace(/[^0-9]/g, "")
        .slice(-6);

      // ￥ + 数字
      const moneyChars = ["￥", ...digits.split("")];

      // 7マスの右側へ配置
      const startIndex = MONEY_BOXES - moneyChars.length;

      moneyChars.forEach((char, index) => {
        boxes[startIndex + index] = char;
      });
    }

    return (
      <div className="kobutsu-money-boxes">
        {boxes.map((char, index) => (
          <span key={index}>
            {char || "\u00A0"}
          </span>
        ))}
      </div>
    );
  };

  const formatDateParts = (value) => {
    if (!value) {
      return {
        year: "",
        month: "",
        day: "",
      };
    }

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) {
      const parts = String(value).split(/[-/]/);

      return {
        year: parts[0] || "",
        month: parts[1] || "",
        day: parts[2] || "",
      };
    }

    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
  };

  const purchaseDate = formatDateParts(date);
  const birthDate = formatDateParts(birth_date);

  /*
   * 元帳デザインに合わせて最低9行表示
   */
  // ========================================
  // 古物台帳印刷用：プライズを合算
  // ========================================
  const isPrizeOrLowPriceKuji = (detail) => {
    const genre1 = detail?.item?.genre1 || "";
    const itemName = detail?.item?.item_name || "";
    const unitPrice = Number(detail?.unit_price || 0);
    const categoryId = Number(detail?.item?.category?.id ?? 0);

    // くじ商品判定
    const isKuji =
      genre1.includes("くじ") ||
      itemName.includes("くじ");

    // くじの場合
    // 3000円未満だけ合算
    // 3000円以上は個別表示
    if (isKuji) {
      return unitPrice < 3000;
    }

    // くじ以外のプライズ判定
    const isPrizeByGenre = genre1.includes("プライズ");
    const isPrizeByCategory = [1, 3].includes(categoryId);

    return isPrizeByGenre || isPrizeByCategory;
  };


  const buildKobutsuPrintDetails = (details = []) => {
    const normalItems = [];
    const prizeItems = [];
    const prizeDisposalItems = [];

    let hasPrizeBoxDamage = false;

    details.forEach((detail) => {
      const conditionId = Number(detail?.condition?.id || 0);
      const conditionName = detail?.condition?.name || "";

      const isDisposal =
        conditionId === 7 ||
        conditionName === "買取不可";

      if (!isPrizeOrLowPriceKuji(detail)) {
        normalItems.push(detail);
        return;
      }

      if (isDisposal) {
        prizeDisposalItems.push(detail);
        return;
      }

      if (
        detail?.box_condition === "箱傷み" ||
        detail?.box_condition === "箱破損"
      ) {
        hasPrizeBoxDamage = true;
      }

      prizeItems.push(detail);
    });


    const result = [...normalItems];


    // 通常プライズ合算
    const prizeQuantity = prizeItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );

    const prizeAmount = prizeItems.reduce(
      (sum, item) => sum + Number(item.subtotal || 0),
      0
    );

    if (prizeQuantity > 0) {
      result.push({
        id: "__KOBUTSU_GROUPED_PRIZE__",
        item: {
          item_name: "プライズ（セットの場合は1点換算）",
        },
        quantity: prizeQuantity,
        subtotal: prizeAmount,
        feature_text: hasPrizeBoxDamage
          ? "箱傷みによる減額有り"
          : "",
      });
    }


    // 買取不可プライズ合算
    const disposalQuantity = prizeDisposalItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );

    if (disposalQuantity > 0) {
      result.push({
        id: "__KOBUTSU_GROUPED_PRIZE_DISPOSAL__",
        item: {
          item_name: "プライズ買取不可商品",
        },
        quantity: disposalQuantity,
        subtotal: 0,
        feature_text: "買取不可",
      });
    }

    return result;
  };

  const printDetails = buildKobutsuPrintDetails(details);

  const ITEMS_PER_PAGE = 9;

  const detailPages = Array.from(
    {
      length: Math.max(
        1,
        Math.ceil(printDetails.length / ITEMS_PER_PAGE)
      ),
    },
    (_, pageIndex) => {
      const start = pageIndex * ITEMS_PER_PAGE;

      const pageItems = printDetails.slice(
        start,
        start + ITEMS_PER_PAGE
      );

      // 各ページ必ず9行にする
      return Array.from(
        { length: ITEMS_PER_PAGE },
        (_, rowIndex) => pageItems[rowIndex] || null
      );
    }
  );

  const identificationList = [
    "運転免許証",
    "健康保険証",
    "パスポート",
    "住民基本台帳",
    "外国人登録証明書",
    "マイナンバーカード",
    "その他",
  ];

  return (
  <div className="kobutsu-print-root">

    {detailPages.map((detailRows, pageIndex) => {
      const isLastPage =
        pageIndex === detailPages.length - 1;

      return (
        <div
          className="kobutsu-print-page"
          key={`page-${pageIndex}`}
        >

      {/* ================================
          上部タイトル
      ================================= */}
      <div className="kobutsu-top">

        <div className="kobutsu-title-box">
          <div className="kobutsu-title">
            買取明細書兼古物台帳
          </div>

          <div className="kobutsu-subtitle">
            古物等の買取
          </div>
        </div>

        <div className="kobutsu-date-area">

          <div className="kobutsu-page-number">
            {pageIndex + 1} / {detailPages.length}
          </div>

          <div className="kobutsu-date-box">
            <span>{purchaseDate.year}</span>
            <small>年</small>

            <span>{purchaseDate.month}</span>
            <small>月</small>

            <span>{purchaseDate.day}</span>
            <small>日</small>
          </div>

        </div>

      </div>


      <div className="kobutsu-message">
        本日付で下記商品を買取いたしました。
      </div>


      {/* ================================
          商品明細
      ================================= */}
      <table className="kobutsu-detail-table">

        <thead>
          <tr>
            <th className="kobutsu-no"></th>
            <th className="kobutsu-item">品　目</th>
            <th className="kobutsu-qty">数量</th>
            <th className="kobutsu-price">金　額</th>
            <th className="kobutsu-feature">特　徴</th>
          </tr>
        </thead>

        <tbody>
          {detailRows.map((detail, index) => (
            <tr key={detail?.id || `empty-${index}`}>

              <td className="kobutsu-number-cell">
                {detail
                  ? pageIndex * ITEMS_PER_PAGE + index + 1
                  : ""}
              </td>

              <td className="kobutsu-item-cell">
                {detail?.item?.item_name || ""}
              </td>

              <td className="kobutsu-center">
                {detail ? detail.quantity : ""}
              </td>

              <td className="kobutsu-money">
                {renderMoneyBoxes(
                  detail ? detail.subtotal : null,
                  !!detail
                )}
              </td>

              <td className="kobutsu-feature-cell">
                {detail
                  ? detail.feature_text ||
                    [
                      detail.condition?.name,
                      detail.box_condition,
                      detail.accessories?.name,
                    ]
                      .filter(Boolean)
                      .join(" / ")
                  : ""}
              </td>
              
            </tr>
          ))}

          <tr className="kobutsu-total-row">

            <th colSpan="2">
              合　計
              <span className="kobutsu-tax-text">
                （消費税込）
              </span>
            </th>

            <td className="kobutsu-center">
              {isLastPage ? total_items : ""}
            </td>

            <td className="kobutsu-money kobutsu-grand-total">
              {renderMoneyBoxes(
                total_amount,
                isLastPage
              )}
            </td>

            <td></td>

          </tr>

        </tbody>

      </table>



      {/* ================================
          注意事項
      ================================= */}
      <div className="kobutsu-confirm-area">

        <div className="kobutsu-confirm-label">
          確<br />認
        </div>

        <div className="kobutsu-confirm-text">
          <div>
            ※お客様にご記入頂いた個人情報は古物営業法に基づき
            記録として保管するものです。
          </div>

          <div>
            ※ご記入が完了した時点で売買契約完了となります。
          </div>

          <div>
            以降のキャンセル・返品等は出来ませんので
            予めご了承下さい。
          </div>

        </div>

      </div>


      {/* ================================
          顧客情報
      ================================= */}
      <div className="kobutsu-customer-wrapper">

        <div className="kobutsu-customer-side-title">
          お<br />
          客<br />
          様<br />
          情<br />
          報
        </div>

        <div className="kobutsu-customer-content">

          {/* 住所 */}
          <div className="kobutsu-info-row kobutsu-address-row">

            <div className="kobutsu-info-label">
              ご住所
            </div>

            <div className="kobutsu-info-value">

              <div className="kobutsu-postal">
                〒 {postal_code || ""}
              </div>

              <div>
                {address || ""}
              </div>

            </div>

          </div>


          {/* 電話 */}
          <div className="kobutsu-info-row">

            <div className="kobutsu-info-label">
              お電話
            </div>

            <div className="kobutsu-info-value">
              {phone_number || ""}
            </div>

          </div>


          {/* 名前・職業 */}
          <div className="kobutsu-name-job-row">

            <div className="kobutsu-name-area">

              <div className="kobutsu-info-label">
                お名前
              </div>

              <div className="kobutsu-name-value">

                <div className="kobutsu-kana">
                  {customerKana}
                </div>

                <div className="kobutsu-name-main">
                  {printName}
                  <span className="kobutsu-sama">
                    様
                  </span>
                </div>

              </div>

            </div>


            <div className="kobutsu-job-area">

              <div className="kobutsu-job-title">
                ご職業
              </div>

              <div className="kobutsu-job-list">
                {printOccupation}
              </div>

            </div>

          </div>


          {/* 生年月日 */}
          <div className="kobutsu-info-row">

            <div className="kobutsu-info-label">
              生年月日
            </div>

            <div className="kobutsu-info-value kobutsu-birthday">

              <span>西暦</span>

              <strong>
                {birthDate.year}
              </strong>
              年

              <strong>
                {birthDate.month}
              </strong>
              月

              <strong>
                {birthDate.day}
              </strong>
              日

              <span className="kobutsu-age">
                （{age || ""}歳）
              </span>

            </div>

          </div>


          {/* インボイス */}
          <div className="kobutsu-info-row">

            <div className="kobutsu-info-label kobutsu-invoice-label">
              適格請求書登録事業者番号
            </div>

            <div className="kobutsu-info-value">
              {printInvoiceNumber || "なし"}
            </div>

          </div>

        </div>

      </div>


      {/* ================================
          身分証
      ================================= */}
      <div className="kobutsu-id-wrapper">

        <div className="kobutsu-id-title">
          身分証明<br />
          確認区分
        </div>

        <div className="kobutsu-id-content">

          <div className="kobutsu-id-list">

            {identificationList.map((type) => (
              <span key={type}>
                <span className="kobutsu-circle">
                  {identification_type === type
                    ? "●"
                    : "○"}
                </span>

                {type}
              </span>
            ))}

          </div>

          <div className="kobutsu-id-number">
            No
          </div>

        </div>

      </div>


      {/* ================================
          下部店舗情報
      ================================= */}
      <div className="kobutsu-footer">

        {/* 店舗情報 */}
        <div className="kobutsu-footer-shop">

          <div className="kobutsu-logo">
            ぱそまる
          </div>

          <div className="kobutsu-shop-info">
            <div>
              〒556-0005 大阪府大阪市浪速区日本橋 4-15-14
            </div>

            <div>
              TEL 06-6636-6382
            </div>
          </div>

        </div>


        {/* お客様署名 */}
        <div className="kobutsu-signature">

          <div className="kobutsu-signature-label">
            署名
          </div>

          <div className="kobutsu-signature-space">
          </div>

        </div>

      </div>

    </div>
      );
    })}

  </div>
);
  
};

export default PrintPurchaseOrderPageShop;